import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Container, Eyebrow, Wordmark } from '@/components/landing/primitives'
import { IntakeForm } from '@/components/landing/intake-form'
import { getFieldsForPlan } from '@/lib/intake'

type IntakeSearch = {
  session_id?: string
}

type SessionData = {
  plan: 'launch' | 'plus'
  customerEmail: string
  sessionId: string
  alreadySubmitted: boolean
}

export const Route = createFileRoute('/intake')({
  validateSearch: (search: Record<string, unknown>): IntakeSearch => ({
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
  }),
  component: IntakePage,
})

function IntakePage() {
  const { session_id: sessionId } = useSearch({ from: '/intake' })
  const [status, setStatus] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; data: SessionData }
  >({ kind: 'loading' })

  useEffect(() => {
    if (!sessionId) {
      setStatus({
        kind: 'error',
        message:
          'No checkout session found. If you just paid, use the link from your confirmation email. Otherwise, email hello@withvello.com.',
      })
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(
          `/api/intake/session?session_id=${encodeURIComponent(sessionId)}`,
        )
        const body = (await res.json().catch(() => ({}))) as
          | SessionData
          | { error?: string }
        if (cancelled) return
        if (!res.ok || !('plan' in body)) {
          const message =
            'error' in body && body.error ? body.error : `Could not verify session (${res.status})`
          setStatus({ kind: 'error', message })
          return
        }
        setStatus({ kind: 'ready', data: body })
      } catch (err) {
        if (cancelled) return
        setStatus({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Network error',
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [sessionId])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '24px 0', borderBottom: '1px solid var(--line)' }}>
        <Container>
          <Wordmark />
        </Container>
      </header>
      <main style={{ flex: 1, padding: '80px 0 120px' }}>
        <Container>
          {status.kind === 'loading' && (
            <div style={{ maxWidth: 560, color: 'var(--muted)' }}>
              <Eyebrow>Verifying your checkout</Eyebrow>
              <p style={{ marginTop: 16, fontSize: 16 }}>One moment…</p>
            </div>
          )}

          {status.kind === 'error' && (
            <div style={{ maxWidth: 560 }}>
              <Eyebrow>Something&rsquo;s off</Eyebrow>
              <h1
                style={{
                  fontFamily: 'var(--serif)',
                  fontSize: 'clamp(32px, 4.5vw, 56px)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.025em',
                  fontWeight: 400,
                  margin: '20px 0 16px',
                }}
              >
                Your intake could not load.
              </h1>
              <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.55 }}>
                {status.message}
              </p>
              <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 24 }}>
                Email{' '}
                <a
                  href="mailto:hello@withvello.com"
                  style={{ borderBottom: '1px solid var(--line-strong)' }}
                >
                  hello@withvello.com
                </a>{' '}
                to get it sorted.
              </p>
            </div>
          )}

          {status.kind === 'ready' && status.data.alreadySubmitted && (
            <div style={{ maxWidth: 560 }}>
              <Eyebrow>Intake received</Eyebrow>
              <h1
                style={{
                  fontFamily: 'var(--serif)',
                  fontSize: 'clamp(40px, 6vw, 72px)',
                  lineHeight: 1.0,
                  letterSpacing: '-0.025em',
                  fontWeight: 400,
                  margin: '20px 0 0',
                }}
              >
                You&rsquo;re all set.
              </h1>
              <p
                style={{
                  fontSize: 18,
                  color: 'var(--muted)',
                  lineHeight: 1.55,
                  marginTop: 24,
                }}
              >
                Your intake is already in. First design round lands on day three. Check
                your inbox for confirmation.
              </p>
            </div>
          )}

          {status.kind === 'ready' && !status.data.alreadySubmitted && (
            <div>
              <div style={{ maxWidth: 680 }}>
                <Eyebrow>Intake &middot; 10 minutes</Eyebrow>
                <h1
                  style={{
                    fontFamily: 'var(--serif)',
                    fontSize: 'clamp(40px, 6vw, 72px)',
                    lineHeight: 1.0,
                    letterSpacing: '-0.025em',
                    fontWeight: 400,
                    margin: '20px 0 0',
                  }}
                >
                  Tell me about your site.
                </h1>
                <p
                  style={{
                    fontSize: 18,
                    color: 'var(--muted)',
                    lineHeight: 1.55,
                    marginTop: 24,
                    maxWidth: 560,
                  }}
                >
                  {status.data.plan === 'launch'
                    ? 'Ten minutes of questions, enough to start designing today. Required fields are marked.'
                    : 'A few quick details to kick off your first edit within 48 hours.'}
                </p>
              </div>
              <IntakeForm
                plan={status.data.plan}
                sessionId={status.data.sessionId}
                customerEmail={status.data.customerEmail}
                fields={getFieldsForPlan(status.data.plan)}
              />
            </div>
          )}
        </Container>
      </main>
    </div>
  )
}
